import React, { useState, useMemo, useCallback, useEffect } from "react";
import AnalyseModal from "../../../modals/AnalyseModal";

import PositionsInfo from "./PositionsInfo";
import ExitIcon from "../../../images/icons/exit.svg";
import ExitDark from "../../../images/icons/export.svg";
import { useTheme } from "@emotion/react";
import EmptyPositions from "./EmptyPositions";
import ShimmerPositionsTable from "./PositionsTableSkeleton";
import PositionsTable from "../components/PositionsTable";

const UnderlyingGroupView = ({
  underlyingBased,
  activeButton,
  symbolData,
  handleClick,
  isExitSelectedEnabledUnderlying,
  modalData,
  symbols,
  handleCallback,
  showToast,
  isFetching,
  hadPositions,
  handleHeaderCheckboxChange,
  handleRowMouseEnter,
  handleRowMouseLeave,
  hoveredRowIndex,
  mainIndex,
  visibility,
  duplicateData,
  handleAddModal,
  handleExitModal,
  handleCheckboxChange,
  formatNumberWithFontWeight,
  calculateLtpPointerPosition,
}) => {
  const theme = useTheme();
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [isAnalyseModalOpen, setIsAnalyseModalOpen] = useState(false);
  const [selectedAnalyseData, setSelectedAnalyseData] = useState(null);

  useEffect(() => {
    if (underlyingBased && underlyingBased.length > 0) {
      const initialExpandedState = {};
      underlyingBased.forEach((_, index) => {
        initialExpandedState[index] = true;
      });
      setExpandedBlocks(initialExpandedState);
    }
  }, [underlyingBased]);

  // Memoize expensive calculations
  const processedUnderlyingData = useMemo(() => {
    if (!underlyingBased || underlyingBased.length === 0) return [];

    return underlyingBased.map((data, index) => {
      let groupNetProfit = 0;
      const hasValidPositions = data?.positions?.some(
        (position) => position.net_quantity !== 0
      );

      // Calculate group net profit
      data?.positions?.forEach((position) => {
        const ltp = symbolData[position?.code]?.ltp;
        if (ltp) {
          const pnl =
            parseFloat(position?.realised_pl) +
            (parseFloat(ltp) - parseFloat(position?.average_prc)) *
            parseFloat(position?.net_quantity);
          groupNetProfit += pnl;
        }
      });

      return {
        ...data,
        groupNetProfit,
        hasValidPositions,
        originalIndex: index,
      };
    });
  }, [underlyingBased, symbolData]);

  // Memoize selected symbol for modal
  const selectedModalSymbol = useMemo(() => {
    if (!selectedAnalyseData || !symbols) return null;
    return symbols.find(
      (option) => option.scrip === selectedAnalyseData?.scrip
    );
  }, [selectedAnalyseData, symbols]);

  // Memoize modal index calculation
  const modalDataIndex = useMemo(() => {
    if (!selectedAnalyseData || !underlyingBased) return -1;
    return underlyingBased.findIndex((item) => item === selectedAnalyseData);
  }, [selectedAnalyseData, underlyingBased]);

  const toggleExpand = useCallback((index) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const handleAnalyseClick = useCallback(
    (data) => {
      setSelectedAnalyseData(data);
      setIsAnalyseModalOpen(true);
      handleClick(data);
    },
    [handleClick]
  );

  const handleAnalyseModalClose = useCallback(() => {
    setIsAnalyseModalOpen(false);
    setSelectedAnalyseData(null);
    handleCallback();
  }, [handleCallback]);

  const handleToastClick = useCallback(
    (index) => {
      showToast("groupbyunderlying", index);
    },
    [showToast]
  );

  if ((!underlyingBased || underlyingBased.length === 0)) {
    // If we are fetching and the user previously had positions, show skeleton instead of empty state
    if (isFetching && hadPositions) {
      return (
        <div className="group-table flex flex-col gap-3 w-full">
          <div className="group-container w-full">
            <ShimmerPositionsTable />
          </div>
        </div>
      );
    }
    return <EmptyPositions />;
  }

  return (
    <>
      {processedUnderlyingData.map((data, index) => {
        const isExpanded = expandedBlocks[index] === true;
        const originalIndex = data.originalIndex;

        return (
          <div
            className="group-table flex flex-col gap-3 w-full"
            key={`group-${originalIndex}`}>
            <div className="group-container w-full">
              <div className="border-0 flex flex-col p-4 gap-3 w-full">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold text-p300">
                    {data?.scrip}{" "}
                    <span className="text-md font-medium text-n600 ml-2">
                      ({data?.positions?.length} positions)
                    </span>
                  </div>

                  <div className="flex gap-3 items-center">
                    <button
                      onClick={() => handleAnalyseClick(data)}
                      className={`bg-swipe-button w-24 h-7 rounded-md flex justify-center items-center gap-2 text-p400 text-xs font-semibold ${data.hasValidPositions
                        ? "enabled"
                        : "disabled opacity-50 cursor-not-allowed"
                        }`}
                      disabled={!data.hasValidPositions}>
                      {theme === "dark" ? (
                        <img src={ExitDark} alt="" />
                      ) : (
                        <img src={ExitIcon} alt="" />
                      )}
                      Analyse
                    </button>

                    <button
                      className={`w-14 h-7 rounded-md flex justify-center items-center gap-2 text-xs font-semibold ${isExitSelectedEnabledUnderlying[originalIndex]
                        ? "bg-p300 text-p400"
                        : "bg-n300 text-p400"
                        }`}
                      onClick={() => handleToastClick(originalIndex)}
                      disabled={
                        !isExitSelectedEnabledUnderlying[originalIndex]
                      }>
                      Exit
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <PositionsTable
                  positions={data.positions}
                  symbolData={symbolData}
                  handleRowMouseEnter={handleRowMouseEnter}
                  handleRowMouseLeave={handleRowMouseLeave}
                  handleCheckboxChange={handleCheckboxChange}
                  hoveredRowIndex={hoveredRowIndex}
                  mainIndex={index}
                  currentGroupIndex={originalIndex}
                  activeButton="groupbyunderlying"
                  visibility={visibility}
                  handleAddModal={handleAddModal}
                  handleExitModal={handleExitModal}
                  formatNumberWithFontWeight={formatNumberWithFontWeight}
                />
              )}

              <PositionsInfo data={data} groupNetProfit={data.groupNetProfit} />

              <div className="flex justify-center mt-2 mb-2">
                <button
                  className="expand-collapse-btn"
                  onClick={() => toggleExpand(index)}>
                  {isExpanded ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-chevron-up-icon lucide-chevron-up">
                      <path d="m18 15-6-6-6 6" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-chevron-down-icon lucide-chevron-down">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Analyse Modal - Only render when open */}
      {isAnalyseModalOpen && selectedAnalyseData && (
        <AnalyseModal
          isOpen={isAnalyseModalOpen}
          onClose={handleAnalyseModalClose}
          data={selectedAnalyseData?.computedData}
          selectedData={selectedAnalyseData?.positions}
          selectedSymbol={selectedModalSymbol}
          type="symbol"
          index={modalDataIndex}
          allOptionData={selectedAnalyseData?.allOptionData}
          closeCallBack={handleAnalyseModalClose}
        />
      )}
    </>
  );
};

export default React.memo(UnderlyingGroupView);
